import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  shape: {
    logoSize: {
      xs: 48,
      sm: 64,
      md: 80,
      lg: 100,
      xl: 120,
    },
  },
  palette: {
    primary: {
      main: '#000000',
      light: '#FAF4D5',
      dark: '#181818',
      contrastText: '#FAE03F',
    },
    secondary: {
      main: '#FAF4D5',
      light: '#FFFFFF',
      dark: '#D4C49A',
      contrastText: '#191919',
    },
    gold: {
      main: '#FAE03F',
      light: '#FAF4D5',
      dark: '#E6B800',
      contrastText: '#000000',
    },
    accent: {
      main: '#ee2a7b',
      light: '#ff5a9a',
      dark: '#c71e65',
      contrastText: '#FFFFFF',
    },
    glow: {
      main: '#F4C940',
      contrastText: '#000000',
    },
    background: {
      default: 'rgba(255, 255, 255, 0)', // Transparent background using rgba format
      paper: 'rgba(255, 255, 255, 0.98)',  // More opaque white for cards
    },
    text: {
      primary: '#181818',
      secondary: '#444444',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    henny: {
      fontFamily: '"Lionel Classic", cursive',
      fontWeight: 400,
      fontSize: '2.5rem',
      letterSpacing: '0.04em',
    },
    h1: {
      fontFamily: '"Lionel Classic", serif',
      fontWeight: 800,
      fontSize: '3.2rem',
      '@media (max-width:1536px)': { fontSize: '3.0rem' },
      '@media (max-width:1200px)': { fontSize: '2.8rem' },
      '@media (max-width:900px)': { fontSize: '2.4rem' },
      '@media (max-width:600px)': { fontSize: '2rem' },
    },
    h2: {
      fontFamily: '"Lionel Classic", serif',
      fontWeight: 600,
      fontSize: '2.5rem',
      '@media (max-width:1536px)': { fontSize: '2.3rem' },
      '@media (max-width:1200px)': { fontSize: '2.1rem' },
      '@media (max-width:900px)': { fontSize: '1.9rem' },
      '@media (max-width:600px)': { fontSize: '1.7rem' },
    },
    h3: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      fontSize: '2.0rem',
      '@media (max-width:1536px)': { fontSize: '1.8rem' },
      '@media (max-width:1200px)': { fontSize: '1.6rem' },
      '@media (max-width:900px)': { fontSize: '1.4rem' },
      '@media (max-width:600px)': { fontSize: '1.1rem' },
    },
    h4: {
      fontFamily: '"Lionel Classic", serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      '@media (max-width:1536px)': { fontSize: '1.4rem' },
      '@media (max-width:1200px)': { fontSize: '1.3rem' },
      '@media (max-width:900px)': { fontSize: '1.2rem' },
      '@media (max-width:600px)': { fontSize: '1.1rem' },
    },
    h5: {
      fontFamily: '"Lionel Classic", serif',
      fontWeight: 500,
      fontSize: '1.25rem',
      '@media (max-width:1536px)': { fontSize: '1.2rem' },
      '@media (max-width:1200px)': { fontSize: '1.15rem' },
      '@media (max-width:900px)': { fontSize: '1.1rem' },
      '@media (max-width:600px)': { fontSize: '1.05rem' },
    },
    h6: {
      fontFamily: '"Lionel Classic", serif',
      fontWeight: 500,
      fontSize: '1rem',
      '@media (max-width:1536px)': { fontSize: '0.95rem' },
      '@media (max-width:1200px)': { fontSize: '0.9rem' },
      '@media (max-width:900px)': { fontSize: '0.85rem' },
      '@media (max-width:600px)': { fontSize: '0.8rem' },
    },
    subtitle1: {
      fontFamily: '"Lionel Classic", serif',
      fontSize: '1.125rem',
      lineHeight: 1.5,
      '@media (max-width:600px)': { fontSize: '1.0625rem' },
    },
    subtitle2: {
      fontFamily: '"Lionel Classic", serif',
      fontSize: '1rem',
      lineHeight: 1.4,
      fontWeight: 600,
    },
    body1: {
      fontFamily: '"Lionel Classic", serif',
      fontSize: '1.0625rem',
      lineHeight: 1.6,
      '@media (max-width:600px)': { fontSize: '1rem' },
    },
    body2: {
      fontFamily: '"Lionel Classic", serif',
      fontSize: '1rem',
      lineHeight: 1.5,
      '@media (max-width:600px)': { fontSize: '0.9375rem' },
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.8125rem',
      lineHeight: 1.4,
    },
    yell:{
      fontFamily: '"Lionel Classic", serif',
      fontWeight: 600,
      fontSize: '8rem',letterSpacing: '0.04em',
      '@media (max-width:1536px)': { fontSize: '5rem' },
      '@media (max-width:1200px)': { fontSize: '2.1rem' },
      '@media (max-width:900px)': { fontSize: '1.9rem' },
      '@media (max-width:600px)': { fontSize: '1.7rem' },
    }
  },
  components: {
    MuiButton: {
      variants: [
        {
          props: { variant: 'containedGradient' },
          style: {
            background: 'linear-gradient(90deg, #FAE03F 0%, #FFF8E1 100%)',
            color: '#000',
            '&:hover': {
              background: 'linear-gradient(90deg, #FFE85C 0%, #FFF2B3 100%)',
              color: '#000'
            }
          }
        },
        {
          props: { variant: 'containedPill' },
          style: {
            backgroundColor: '#000',
            color: '#fff',
            borderRadius: 9999,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 32px',
            fontSize: '1.125rem',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#222',
              color: '#fff',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
            }
          }
        }
      ],
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          '@media (max-width:600px)': {
            padding: '8px 16px',
            fontSize: '1rem',
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.875rem',
          lineHeight: 1,
          minHeight: 'auto'
        },
        sizeMedium: {
          padding: '10px 24px',
          fontSize: '1.0625rem',
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1.25rem',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
          border: '1px solid rgba(250, 224, 63, 0.2)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (min-width:600px)': {
            paddingLeft: 32,
            paddingRight: 32,
          },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          '&.logo-glow': {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            padding: '6px',
            border: '2px solid',
            borderColor: 'glow.main',
            boxShadow: '0 0 12px rgba(250, 224, 63, 0.3)',
            backgroundColor: 'background.paper',
            transition: 'all 0.3s ease-in-out',
          }
          ,
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: '#F4C940', // or theme.palette.glow.main
        },
        iconHover: {
          color: '#FFD700',
        },
      },
    },
  },
});

export default theme;